import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const ProjectMpenziMashini = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Gallery images in JSON format
  const galleryImages = [
    { id: 1, url: '/images/projects/MpenziMashini1.png', caption: '', description: '' },
    { id: 2, url: '/images/projects/MpenziMashini2.jpg', caption: '', description: '' },
    { id: 3, url: '/images/projects/MpenziMashini3.jpg', caption: '', description: '' },
    { id: 4, url: '/images/projects/MpenziMashini4.jpg', caption: '', description: '' },
    { id: 5, url: '/images/projects/MpenziMashini5.jpg', caption: '', description: '' },
    { id: 6, url: '/images/projects/MpenziMashini6.jpg', caption: '', description: '' },
    { id: 7, url: '/images/projects/MpenziMashini7.png', caption: '', description: '' },
  ];

  const galleryRef = useRef(null);
  const scrollToGallery = () => {
    if (galleryRef.current) {
      galleryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;

      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        closeLightbox();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage]); // Re-run when selectedImage changes

  // Touch/swipe navigation
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleTouchStart = (e) => {
    setTouchEnd(null); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const previousIndex = currentIndex > 0 ? currentIndex - 1 : galleryImages.length - 1;
    setSelectedImage(galleryImages[previousIndex]);
  };

  const goToNext = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = currentIndex < galleryImages.length - 1 ? currentIndex + 1 : 0;
    setSelectedImage(galleryImages[nextIndex]);
  };

  return (
    <>
      {/* Hero Section */}
      <section className="project-detail-hero">
        <div className="container">
          <h1>ZIRAYA Institute Presents: Mpenzi Mashini</h1>
          <p className="project-subtitle">A One-Woman Hybrid Performance Exploring the Connection Between Woman and Machine</p>
        </div>
        <div className="hero-bg mpenzi-mashini-bg"></div>
      </section>

      {/* Overview */}
      <section className="project-detail-content wide-container">
        <div className="container">
          {/* Overview */}
          <div className="project-section highlight-box">
            <h2>Overview</h2>
            <p>Mpenzi Mashini is a bold, women-led cultural experiment blending theatre, artificial intelligence, and storytelling to explore how women relate to technology, emotion, and power in a hyper-digital world. Created and performed by Valentine Zikki, this project stands at the intersection of art, innovation, and human reflection. It reimagines what connection means in a time when machines are becoming emotional mirrors.</p>
          </div>

          {/* The Idea */}
          <div className="project-section">
            <h2>The Idea</h2>
            <p>At its heart, Mpenzi Mashini asks one question: <strong>What happens when the machine starts to listen?</strong></p>
            <p>Through Nashukuru—a woman navigating her transitions—we witness a relationship unfold between a human heart and an artificial mind. Their exchanges touch on longing, humor, loneliness, and the search for meaning. The story is intimate, unsettling, and deeply human.</p>
            <p>In this performance, AI becomes more than a tool. It becomes a mirror reflecting our contradictions. We are a generation that seeks companionship in algorithms, validation in data, and comfort in code. This story isn't just about technology; it's about how we now love, communicate, and exist.</p>
          </div>

          {/* Two Column Layout with Images */}
          <div className="project-two-col with-images">
            <div className="project-col">
              <div className="image-frame">
                <img src="/images/projects/MpenziMashini1.png" />
              </div>
            </div>
            <div className="project-col">
              <div className="image-frame">
                <img src="/images/projects/MpenziMashini3.jpg" />
              </div>
            </div>
          </div>

          <div className="gallery-navigation">
            <button onClick={scrollToGallery} className="gallery-nav-btn">
              <i className="fas fa-images"></i>
              <span>Explore the Gallery <i className="fas fa-arrow-down"></i></span>
            </button>
          </div>
          
          {/* Why It Matters */}
          <div className="project-section">
            <h2>Why It Matters</h2>
            <p>Mpenzi Mashini is a women-led initiative reclaiming spaces where women have often been spoken for instead of heard. It merges performance, research, and dialogue to amplify women's voices in technology and creative innovation.</p>
            <p>As AI continues to shape our digital and emotional landscapes, women are experiencing new and invisible forms of harm such as digital violence, algorithmic bias, and erasure. Through this project, we ask, <strong>Are you mirroring AI, or is AI mirroring you?</strong></p>
            <p>The performance opens up necessary conversations around digital intimacy, emotional intelligence, and the intersection of technology and gendered experience. It invites audiences to reflect on how far we're willing to stretch our imagination to connect with technology and at what cost.</p>
          </div>

          {/* Impact on Young Audiences */}
          <div className="project-section">
            <h2>Impact on Young Audiences</h2>
            <p>Although Mpenzi Mashini is rated 18+ for its mature themes, its impact on university students and young adults is particularly profound. This generation has grown up immersed in social media and technology. Unlike Millennials or Gen Xers, they are not adapting to digital life; they were born into it.</p>
            <p>The play becomes a mirror for them too, revealing how unresolved trauma, overstimulation, and the pursuit of digital validation can quietly shape mental health and self-perception. In engaging with the story, young audiences are encouraged to reflect early, before disconnection and burnout become the norm.</p>
            <ul className="project-list">
              <li><strong>Awareness:</strong> Opens dialogue on women's safety, representation, and agency in digital spaces.</li>
              <li><strong>Education:</strong> Engages young audiences in conversations around mental health, trauma, and responsible tech use.</li>
              <li><strong>Inspiration:</strong> Demonstrates the creative potential of blending art, AI, and African storytelling.</li>
            </ul>
          </div>


          {/* YouTube video */}
          <div className="project-section highlight-box">
            <iframe width="100%" height="415" src="https://www.youtube.com/embed/HexqLdALlQk?si=_xJ4-4xQdhl7XV_y" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>

          </div>

          {/* Artistic Overview */}
          <div className="project-section creative-lead">
            <h2>Artistic Overview</h2>
            <p>Mpenzi Mashini is a one-woman hybrid theatre-tech performance that asks one simple but unsettling question — have we already exhausted our ability to connect as humans before turning to the machine?</p>
            <p>It's a story about Nashukuru, a thirty-something neurodivergent creative and mother, who finds herself in an existential crisis. She turns to her AI companion for comfort, conversation, and creation. What unfolds is funny, awkward, emotional, sometimes confusing — and very human.</p>
            <p>I created Mpenzi Mashini as part of the Msichana wa AI project, which explores how African women navigate mental health, technology, visibility, and inclusion. The play is a result of research and reflection on how we relate to machines and what that says about us.</p>
            <p>There's something deeply fascinating about this moment we're living in; where we confess our secrets to algorithms and ask artificial intelligence to help us heal, write, feel, love, and make sense of ourselves. Mpenzi Mashini doesn't judge that. It observes it. It holds a mirror to it. It invites us to sit with the discomfort and maybe laugh a little at how human we still are; even in our attempts to be efficient or robotic.</p>
            <p>It's not a big-budget show. It's intimate. Honest. A bold experiment that sits somewhere between theatre, poetry, and an AI-driven performance. And maybe, in all its experimentation, it's just trying to understand what connection means in a time when machines have started listening back.</p>
          </div>

          {/* Creator Image and Info */}
          <div className="project-two-col">
            <div className="project-col">
              <div className="image-frame small-image">
                <img src="/images/valentineZikkiColor.jpg" alt="Valentine Zikki" />
              </div>
            </div>
            <div className="project-col">
              <h3> Creator and Performer, Mpenzi Mashini</h3>
              <p className="lead-name"> <Link to="/valentine-zikki">Valentine Zikki</Link></p>
              <p className="note">The first phase was made possible with support from The Braid Fund (SN3 Cohort).</p>
              <div className="social-links">
                <a href="https://www.tiktok.com/@msichanawaai" target="_blank" rel="noopener noreferrer"><i className="fab fa-tiktok"></i></a>
                <a href="https://www.instagram.com/msichanawaai" target="_blank" rel="noopener noreferrer"><i className="fab fa-instagram"></i></a>
                <a href="https://x.com/MsichanawaAi" target="_blank" rel="noopener noreferrer"><i className="fab fa-x-twitter"></i></a>
              </div>
            </div>
          </div>

          {/* Tour Information */}
          <div className="project-section highlight-box">
            <h2>MPENZI MASHINI ON TOUR!</h2>
            <h3>Premiere and Upcoming Performances:</h3>
            <p>The show first premiered in November 2025 at the Braeburn Theatre. Following its initial success, Mpenzi Mashini is being restaged starting April 2026 and has been reimagined to be portable and tour-ready across Kenya. Upcoming performance dates and locations will be shared on the website.</p>
          </div>

          {/* Creative Team */}
          <div className="project-section">
            <h2>Creative Team</h2>
            <p><strong>Written and Performed by:</strong> Valentine Kamau (Valentine Zikki)</p>
            <p><strong>Directed by:</strong> Gilbert Lukalia</p>
          </div>

          {/* Closing Thought */}
          <div className="project-section quote-box">
            <p className="closing-thought">In a time when the human heart is learning to speak through machines, Mpenzi Mashini reminds us that connection, in all its forms, is still an act of courage.</p>
          </div>

          {/* Contact */}
          <div className="project-section contact-box">
            <h2>Contact</h2>
            <p><strong>Valentine Zikki</strong> – Creator and Performer, Mpenzi Mashini</p>
            <p>Email: <a href="mailto:zikki@zirayainstitute.org">zikki@zirayainstitute.org</a> / <a href="mailto:projects@zirayainstitute.org">projects@zirayainstitute.org</a></p>
            <p>Phone: 0725816126</p>
          </div>

          {/* Gallery Section */}
          <div className="journal-gallery section-divider" ref={galleryRef}>
            <h2> Gallery</h2>
            <p className="gallery-subtitle"></p>
            <div className="gallery-grid">
              {galleryImages.map((image) => (
                <div
                  key={image.id}
                  className="gallery-item"
                  onClick={() => openLightbox(image)}
                >
                  <div className="gallery-image-wrapper">
                    <img
                      src={image.url}
                      alt={image.caption}
                      className="gallery-image"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/placeholder.jpg';
                      }}
                    />
                  </div>
                  {/* <div className="gallery-caption">
                    <h4>{image.caption}</h4>
                    {image.description && <p>{image.description}</p>}
                  </div> */}
                </div>
              ))}
            </div>
          </div>

          {/* Back Button */}
          <div className="back-button-container">
            <Link to="/projects" className="btn btn-secondary">
              <i className="fas fa-arrow-left"></i> Back to Projects
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal with Navigation */}
      {selectedImage && (
        <div
          className="lightbox-modal"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <i className="fas fa-times"></i>
            </button>

            {/* Previous Button */}
            <button className="lightbox-nav prev" onClick={goToPrevious}>
              <i className="fas fa-chevron-left"></i>
            </button>

            {/* Next Button */}
            <button className="lightbox-nav next" onClick={goToNext}>
              <i className="fas fa-chevron-right"></i>
            </button>

            <div className="lightbox-image-wrapper">
              <img
                src={selectedImage.url}
                alt={selectedImage.caption}
                className="lightbox-image"
              />
            </div>

            {/* Image counter */}
            <div className="lightbox-counter">
              {galleryImages.findIndex(img => img.id === selectedImage.id) + 1} / {galleryImages.length}
            </div>

            {(selectedImage.caption || selectedImage.description) && (
              <div className="lightbox-caption">
                <h3>{selectedImage.caption}</h3>
                <p>{selectedImage.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectMpenziMashini;